import { z } from "zod";

export const INVOICE_BLOCKS = [
  { id: "address", label: "Kundenadresse", x: 15, y: 66, width: 88, height: 22 },
  { id: "documentInfo", label: "Rechnungsinformationen", x: 110, y: 52, width: 85, height: 38 },
  { id: "contact", label: "Bearbeiter", x: 15, y: 112, width: 180, height: 7 },
  { id: "identifiers", label: "UID und Dienstgebernummer", x: 15, y: 121, width: 180, height: 15 },
  { id: "references", label: "Auftrag und Bauvorhaben", x: 15, y: 138, width: 180, height: 10 },
  { id: "intro", label: "Rechnungstext", x: 15, y: 150, width: 180, height: 12 },
  { id: "servicePeriod", label: "Leistungszeitraum", x: 15, y: 164, width: 180, height: 7 },
  { id: "summary", label: "Rechnungsübersicht und Beträge", x: 15, y: 175, width: 180, height: 53 },
  { id: "paymentTerms", label: "Zahlungsbedingungen", x: 15, y: 230, width: 180, height: 8 },
  { id: "notes", label: "Hinweise", x: 15, y: 240, width: 180, height: 8 },
  { id: "companyInfo", label: "Firmen- und Bankangaben", x: 15, y: 249, width: 180, height: 14 },
  { id: "paymentQr", label: "Zahlungs-QR-Code", x: 155, y: 221, width: 40, height: 44 },
] as const;
export type InvoiceBlockId = typeof INVOICE_BLOCKS[number]["id"];
export const invoiceBlockOffsetSchema = z.object({
  x: z.number().finite().min(-180).max(180),
  y: z.number().finite().min(-250).max(250),
});
export const invoiceLayoutSchema = z.object(Object.fromEntries(
  INVOICE_BLOCKS.map((block) => [block.id, invoiceBlockOffsetSchema.optional()]),
) as Record<InvoiceBlockId, z.ZodOptional<typeof invoiceBlockOffsetSchema>>);
export type InvoiceLayout = z.infer<typeof invoiceLayoutSchema>;

export const invoiceLayoutTemplateInputSchema = z.object({
  name: z.string().trim().min(1, "Bitte einen Vorlagennamen angeben").max(100),
  layout: invoiceLayoutSchema.strict(),
}).strict();
export type InvoiceBounds = {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
};
export type InvoiceRenderedBlock = InvoiceBounds & { id: InvoiceBlockId };
export type InvoiceLayoutWarning = {
  page: number;
  block: InvoiceBlockId;
  other: InvoiceBlockId | "header" | "footer" | "page";
  message: string;
};
export type InvoiceLayoutReport = {
  blocks: InvoiceRenderedBlock[];
  warnings: InvoiceLayoutWarning[];
  pageCount: number;
};

/** Test individual painted fragments, not a bounding box spanning blank space. */
export function findInvoiceLayoutWarnings(
  blocks: InvoiceRenderedBlock[],
  protectedAreas: (InvoiceBounds & { id: "header" | "footer" })[],
  pageWidth = 210,
  pageHeight = 297,
): InvoiceLayoutWarning[] {
  const warnings: InvoiceLayoutWarning[] = [];
  const seen = new Set<string>();
  const label = (id: InvoiceBlockId) => INVOICE_BLOCKS.find((block) => block.id === id)!.label;
  const intersects = (a: InvoiceBounds, b: InvoiceBounds) =>
    a.page === b.page &&
    Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x) > 0.15 &&
    Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y) > 0.15;
  const add = (block: InvoiceRenderedBlock, other: InvoiceLayoutWarning["other"]) => {
    const key = `${block.page}:${[block.id, other].sort().join(":")}`;
    if (seen.has(key)) return;
    seen.add(key);
    const conflict = other === "header" ? "überlappt mit dem Kopfbereich"
      : other === "footer" ? "überlappt mit der Fußzeile"
      : other === "page" ? "ragt über den Seitenrand hinaus"
      : `überlappt mit „${label(other)}“`;
    warnings.push({ page: block.page, block: block.id, other,
      message: `Seite ${block.page}: „${label(block.id)}“ ${conflict}.` });
  };
  blocks.forEach((block, index) => {
    if (block.x < -0.15 || block.y < -0.15 ||
      block.x + block.width > pageWidth + 0.15 || block.y + block.height > pageHeight + 0.15) add(block, "page");
    protectedAreas.forEach((area) => { if (intersects(block, area)) add(block, area.id); });
    for (let i = index + 1; i < blocks.length; i++) {
      if (block.id !== blocks[i].id && intersects(block, blocks[i])) add(block, blocks[i].id);
    }
  });
  return warnings;
}

export type InvoiceLayoutTemplate = z.infer<typeof invoiceLayoutTemplateSchema>;

export const invoiceLayoutTemplateSchema = invoiceLayoutTemplateInputSchema.extend({
  id: z.string().uuid(),
});

export const invoiceLayoutTemplateRenameSchema = invoiceLayoutTemplateInputSchema.pick({ name: true });
